import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMonitoredConversationDetail } from '../../services/adminApi';
import useMonitoringWebSocket from '../hooks/useMonitoringWebSocket';
import './MonitoringDashboard.css';

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  facebook_dm: 'Facebook DM',
  facebook_comment: 'Facebook commenti',
  instagram_dm: 'Instagram DM',
  instagram_comment: 'Instagram commenti',
};

function sentimentBadgeClass(s) {
  if (s === 'positive') return 'pos';
  if (s === 'negative') return 'neg';
  if (s === 'mixed') return 'mix';
  return 'neu';
}

export default function MonitoringConversationDetail() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMonitoredConversationDetail(conversationId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load conversation detail:', err);
      setError(
        err.response?.data?.detail ||
          'Impossibile caricare il dettaglio conversazione.'
      );
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  useMonitoringWebSocket(
    useCallback(
      (msg) => {
        if (
          msg?.type === 'new_message' &&
          String(msg.data?.conversation_id) === String(conversationId)
        ) {
          // Reload thread when this specific conversation receives a
          // new message.
          load();
        }
      },
      [conversationId, load]
    )
  );

  if (loading && !data) {
    return (
      <div className="monitoring-page">
        <div>Caricamento…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="monitoring-page">
        <div className="error-banner">{error}</div>
        <button className="btn" onClick={() => navigate('/admin/monitoring/conversations')}>
          ← Torna alla lista
        </button>
      </div>
    );
  }
  if (!data) return null;

  const conv = data.conversation;
  const isComment =
    conv.channel === 'facebook_comment' || conv.channel === 'instagram_comment';

  return (
    <div className="monitoring-page">
      <button
        className="btn"
        onClick={() => navigate('/admin/monitoring/conversations')}
        style={{ marginBottom: 12 }}
      >
        ← Torna alla lista
      </button>

      <h1>
        Conversazione #{conv.id}
        <span className="badge channel" style={{ marginLeft: 12 }}>
          {CHANNEL_LABELS[conv.channel] || conv.channel}
        </span>
        {conv.is_problematic && (
          <span className="badge problematic" style={{ marginLeft: 8 }}>
            problematica
          </span>
        )}
      </h1>
      <div className="subtitle">
        Contatto: <strong>{conv.contact_name || conv.contact_id}</strong>
        {' · '}
        Owner:{' '}
        {conv.owner ? (
          <a
            href={`/admin/users/${conv.owner.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/admin/users/${conv.owner.id}`);
            }}
          >
            {conv.owner.full_name || conv.owner.email} (#{conv.owner.id})
          </a>
        ) : (
          `#${conv.user_id}`
        )}
      </div>

      {conv.is_problematic && conv.problematic_reasons?.length > 0 && (
        <div className="section" style={{ borderColor: '#c14545' }}>
          <h2 style={{ color: '#c14545' }}>Motivi della segnalazione</h2>
          <div className="problematic-reasons">
            {conv.problematic_reasons.map((r) => (
              <span className="badge" key={r}>
                {r}
              </span>
            ))}
          </div>
        </div>
      )}

      {isComment && (
        <div className="section" style={{ background: '#f7f8fa' }}>
          <strong>Conversazione di commenti.</strong> Per moderare i commenti
          usa la pagina dedicata{' '}
          <a
            href="/admin/orphan-comments"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/orphan-comments');
            }}
          >
            Orphan Comments
          </a>{' '}
          o l'area social per il singolo account.
        </div>
      )}

      <div className="section">
        <h2>
          Stato
          <span className={`badge ${sentimentBadgeClass(conv.overall_sentiment)}`} style={{ marginLeft: 10 }}>
            sentiment: {conv.overall_sentiment || '—'}
          </span>
          {conv.active_escalations_count > 0 && (
            <span className="badge escalated" style={{ marginLeft: 6 }}>
              esc {conv.active_escalations_count}
            </span>
          )}
          {conv.sla_breached && (
            <span className="badge sla" style={{ marginLeft: 6 }}>
              SLA superato
            </span>
          )}
        </h2>
        <div style={{ fontSize: 13, color: '#444' }}>
          Messaggi totali: {conv.message_count} · Punteggio sentiment medio:{' '}
          {conv.average_sentiment_score?.toFixed(2) ?? '—'} ·{' '}
          AI in pausa: {conv.ai_paused ? 'Sì' : 'No'} ·{' '}
          Ultimo messaggio:{' '}
          {conv.last_message_at
            ? new Date(conv.last_message_at).toLocaleString('it-IT')
            : '—'}
        </div>
      </div>

      <div className="section">
        <h2>Thread messaggi</h2>
        {data.messages.length === 0 ? (
          <div className="empty-state">Nessun messaggio.</div>
        ) : (
          <div className="thread">
            {data.messages.map((m) => (
              <div className={`msg ${m.direction}`} key={m.id}>
                <div>{m.message_text}</div>
                <div className="meta">
                  {m.sentiment ? (
                    <span className={`badge ${sentimentBadgeClass(m.sentiment)}`}>
                      {m.sentiment}
                    </span>
                  ) : null}{' '}
                  {m.sender_type ? `· ${m.sender_type}` : ''}{' '}
                  ·{' '}
                  {m.created_at
                    ? new Date(m.created_at).toLocaleString('it-IT')
                    : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section">
        <h2>Timeline escalation</h2>
        {data.escalations.length === 0 ? (
          <div className="empty-state">Nessuna escalation collegata.</div>
        ) : (
          <div className="escalation-timeline">
            {data.escalations.map((e) => (
              <div className="item" key={e.id}>
                <div>
                  <strong>#{e.id}</strong> · {e.type} · stato{' '}
                  <em>{e.status}</em> · priorità {e.priority || '—'}
                  {e.sla_breached ? (
                    <span className="badge sla" style={{ marginLeft: 8 }}>
                      SLA
                    </span>
                  ) : null}
                </div>
                {e.assigned_to && (
                  <div className="when">Assegnata a: {e.assigned_to}</div>
                )}
                {e.trigger_message && (
                  <div className="when">
                    Trigger: {e.trigger_message.slice(0, 200)}
                  </div>
                )}
                <div className="when">
                  Creata:{' '}
                  {e.created_at
                    ? new Date(e.created_at).toLocaleString('it-IT')
                    : '—'}
                  {e.sla_deadline ? ' · scadenza SLA: ' + new Date(e.sla_deadline).toLocaleString('it-IT') : ''}
                  {e.resolved_at
                    ? ' · risolta: ' +
                      new Date(e.resolved_at).toLocaleString('it-IT')
                    : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
